// Service for reading top pick data from JSON file
export interface StockAnalysis {
  stock_ticker: string;
  stock_name: string;
  analysis_date: string;
  bullish_score: number;
  current_price: number;
  change_30_day: number | null;
  week_change: number | null;
  month_change: number | null;
  rsi: number | null;
  macd_status: string;
  pattern_score: number;
  ml_prediction: string | null;
  ml_accuracy: number;
  sentiment_score: number;
  support_level: number | null;
  pivot_level: number | null;
  resistance_level: number | null;
  score_tier: string;
  intraday_signal: string;
  entry_zone: string;
  risk_reward: string;
  volume_ratio: number;
  momentum_3d: number;
  market_regime: string;
  analysis_text: string;
}

export interface TopPickSimplified {
  ticker: string;
  score: number;
  tier: string;
  signal: string;
  entry: string;
  price: number;
  risk_reward: string;
  volume_ratio: number;
  momentum_3d: number;
  regime: string;
  week_change: number | null;
  month_change: number | null;
  pattern_score: number;
  ml_prediction: string | null;
  ml_accuracy: number;
  sentiment: number;
  rsi: number | null;
  macd_status: string | null;
  support_level: number | null;
  pivot_level: number | null;
  resistance_level: number | null;
}

export interface TopPicksData {
  last_updated: string;
  top_pick: StockAnalysis;
  top_picks: TopPickSimplified[];
  us_markets: TopPickSimplified[];
  indian_markets: TopPickSimplified[];
}

// Cache for the JSON data
let cachedData: StockAnalysis | null = null;
let cachedFullData: TopPicksData | null = null;
let lastFetchTime = 0;
const CACHE_DURATION = 30000; // 30 seconds

export const fetchTopBullishPick = async (): Promise<StockAnalysis | null> => {
  try {
    const now = Date.now();
    
    // Return cached data if it's still fresh
    if (cachedData && (now - lastFetchTime) < CACHE_DURATION) {
      return cachedData;
    }

    // Fetch directly from S3 bucket with timestamp to prevent caching
    const response = await fetch(`https://s3.ap-south-1.amazonaws.com/christopheranto.com/top_pick/latest_pick.json?t=${now}`, {
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const fullData: TopPicksData = await response.json();
    const data: StockAnalysis = fullData.top_pick;

    // Update cache
    cachedData = data;
    cachedFullData = fullData;
    lastFetchTime = now;
    
    return data;
  } catch (error) {
    console.error('Error fetching top pick from JSON:', error);
    return null;
  }
};

export const fetchTopPicksData = async (): Promise<TopPicksData | null> => {
  try {
    const now = Date.now();
    
    // Return cached data if it's still fresh
    if (cachedFullData && (now - lastFetchTime) < CACHE_DURATION) {
      return cachedFullData;
    }

    // Fetch directly from S3 bucket with timestamp to prevent caching
    const response = await fetch(`https://s3.ap-south-1.amazonaws.com/christopheranto.com/top_pick/latest_pick.json?t=${now}`, {
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: TopPicksData = await response.json();

    // Update cache
    cachedData = data.top_pick;
    cachedFullData = data;
    lastFetchTime = now;
    
    return data;
  } catch (error) {
    console.error('Error fetching top picks data from JSON:', error);
    return null;
  }
};

// Function to check if the JSON file has been updated
export const checkForUpdates = async (): Promise<boolean> => {
  try {
    if (!cachedData) return true; // If no cached data, consider it an update
    
    const response = await fetch(`https://s3.ap-south-1.amazonaws.com/christopheranto.com/top_pick/latest_pick.json?t=${Date.now()}`, {
      method: 'HEAD', // Only get headers, not the full file
      headers: {
        'Cache-Control': 'no-cache'
      }
    });

    if (response.ok) {
      const lastModified = response.headers.get('Last-Modified');
      if (lastModified) {
        const fileModifiedTime = new Date(lastModified).getTime();
        return fileModifiedTime > lastFetchTime;
      }
    }
    
    // Fallback: always check for updates if we can't determine file modification time
    return true;
  } catch (error) {
    console.error('Error checking for updates:', error);
    return false;
  }
};
